import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type { Note } from '@elena/shared';
import { api, ApiError } from '../lib/api';
import { Icon } from '../components/Icon';
import { useElena } from '../state';

const DEFAULT_LIST = 'Beleške';

const when = (iso: string) =>
  new Date(iso).toLocaleDateString('sr-Latn-RS', {
    timeZone: 'Europe/Belgrade',
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export function Notes() {
  const { activity } = useElena();
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [lists, setLists] = useState<string[]>([]);
  const [active, setActive] = useState<string>(DEFAULT_LIST);
  const [text, setText] = useState('');
  const [newList, setNewList] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.notes();
      setNotes(r.notes);
      setLists(r.lists);
    } catch {
      setNotes([]);
    }
  }, []);

  // Notes written by voice show up right away.
  useEffect(() => {
    void load();
  }, [load, activity.length]);

  const tabs = useMemo(() => {
    const all = lists.includes(DEFAULT_LIST) ? lists : [DEFAULT_LIST, ...lists];
    return all.includes(active) ? all : [...all, active];
  }, [lists, active]);

  const shown = (notes ?? [])
    .filter((n) => n.list === active)
    .sort((a, b) => Number(a.done) - Number(b.done) || b.createdAt.localeCompare(a.createdAt));
  const open = shown.filter((n) => !n.done).length;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setError(null);
    try {
      await api.addNote(text.trim(), active === DEFAULT_LIST ? undefined : active);
      setText('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Beleška nije sačuvana.');
    }
  };

  const createList = (e: FormEvent) => {
    e.preventDefault();
    const name = newList.trim();
    if (!name) return;
    setActive(name.charAt(0).toUpperCase() + name.slice(1));
    setNewList('');
  };

  const patch = async (n: Note, change: { done?: boolean; text?: string }) => {
    setNotes((all) => all?.map((x) => (x.id === n.id ? { ...x, ...change } : x)) ?? null);
    await api.updateNote(n.id, change).catch(() => undefined);
    await load();
  };

  const remove = async (n: Note) => {
    setNotes((all) => all?.filter((x) => x.id !== n.id) ?? null);
    await api.removeNote(n.id).catch(() => undefined);
    await load();
  };

  const clearDone = async () => {
    await Promise.all(
      shown.filter((n) => n.done).map((n) => api.removeNote(n.id).catch(() => undefined)),
    );
    await load();
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Beleške</h1>
          <p className="muted">
            Reci „zapiši da treba da kupim punjač“ ili „dodaj mleko na spisak za kupovinu“. Pitaj
            „šta je na spisku za kupovinu“ i Elena ti pročita.
          </p>
        </div>
      </header>

      <div className="notes-layout">
        <nav className="card notes-lists" aria-label="Spiskovi">
          <ul className="list">
            {tabs.map((l) => {
              const count = (notes ?? []).filter((n) => n.list === l && !n.done).length;
              return (
                <li key={l}>
                  <button
                    type="button"
                    className={`notes-tab${l === active ? ' is-on' : ''}`}
                    aria-current={l === active ? 'true' : undefined}
                    onClick={() => setActive(l)}
                  >
                    <Icon name={l === DEFAULT_LIST ? 'note' : 'check'} size={16} />
                    <span className="grow">{l}</span>
                    {count > 0 && <span className="count">{count}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          <form className="row gap-8" onSubmit={createList}>
            <label className="sr-only" htmlFor="new-list">
              Novi spisak
            </label>
            <input
              id="new-list"
              className="field small-field"
              placeholder="Novi spisak"
              value={newList}
              maxLength={40}
              onChange={(e) => setNewList(e.target.value)}
            />
            <button type="submit" className="icon-btn ghost" aria-label="Napravi spisak">
              <Icon name="plus" />
            </button>
          </form>
        </nav>

        <section className="card" aria-labelledby="notes-title">
          <header className="card-head">
            <h2 id="notes-title">{active}</h2>
            <span className="muted small">
              {open} {open === 1 ? 'stavka' : 'stavki'}
            </span>
          </header>

          <form className="row gap-8" onSubmit={add}>
            <label className="sr-only" htmlFor="note-text">
              Nova beleška
            </label>
            <input
              id="note-text"
              className="field"
              placeholder={active === DEFAULT_LIST ? 'Zapiši nešto…' : `Dodaj na spisak ${active}…`}
              value={text}
              maxLength={1000}
              onChange={(e) => setText(e.target.value)}
            />
            <button type="submit" className="btn primary" disabled={!text.trim()}>
              Dodaj
            </button>
          </form>
          {error && <p className="notice">{error}</p>}

          {notes && shown.length === 0 && (
            <p className="muted">
              {active === DEFAULT_LIST
                ? 'Još nema beležaka. Reci Eleni „zapiši…“ ili ukucaj gore.'
                : 'Spisak je prazan.'}
            </p>
          )}

          <ul className="list notes">
            {shown.map((n) => (
              <li key={n.id} className={`list-row note-row${n.done ? ' is-done' : ''}`}>
                <input
                  type="checkbox"
                  className="note-check"
                  checked={n.done}
                  onChange={() => void patch(n, { done: !n.done })}
                  aria-label={n.done ? `Vrati: ${n.text}` : `Precrtaj: ${n.text}`}
                />
                <span className="grow note-text">
                  {n.text}
                  <span className="muted small block">{when(n.createdAt)}</span>
                </span>
                <button
                  type="button"
                  className="icon-btn ghost"
                  aria-label={`Obriši: ${n.text}`}
                  onClick={() => void remove(n)}
                >
                  <Icon name="trash" size={16} />
                </button>
              </li>
            ))}
          </ul>

          {shown.some((n) => n.done) && (
            <button type="button" className="btn self-start" onClick={() => void clearDone()}>
              Obriši precrtane
            </button>
          )}
        </section>
      </div>
    </div>
  );
}
